<template>
  <!-- Single Badge Design - Shows highest priority status with compact engagement indicators -->
  <div class="inline-flex items-center gap-1.5">
    
    <!-- Main Status Badge -->
    <!-- Resent (Shown after bounced email is resent) -->
    <span
      v-if="tracking.resent"
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30 whitespace-nowrap"
      title="Email was resent after bouncing"
    >
      <svg class="w-3.5 h-3.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
      </svg>
      Resent
    </span>
    
    <!-- Bounced (Highest Priority) -->
    <div v-else-if="tracking.bounced" class="inline-flex items-center gap-2">
      <span
        class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30 whitespace-nowrap cursor-help"
        :title="tracking.bounce_reason || 'Email could not be delivered'"
      >
        <svg class="w-3.5 h-3.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>Bounced</span>
      </span>
      
      <!-- Resend Button -->
      <button
        @click="handleResend"
        class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isResending"
        title="Resend email notification"
      >
        <svg
          class="w-3.5 h-3.5"
          :class="{ 'animate-spin': isResending }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        {{ isResending ? 'Sending...' : 'Resend' }}
      </button>
    </div>
    
    <!-- Dropped -->
    <span
      v-else-if="tracking.dropped"
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-500/20 text-gray-300 border border-gray-500/30 whitespace-nowrap"
      title="Email dropped by SendGrid"
    >
      <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clip-rule="evenodd"/>
      </svg>
      Dropped
    </span>
    
    <!-- Clicked (Best Engagement) -->
    <span
      v-else-if="tracking.clicked"
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 whitespace-nowrap"
      :title="`Clicked ${tracking.click_count} time(s)`"
    >
      <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
      </svg>
      Clicked ({{ tracking.click_count }})
    </span>
    
    <!-- Opened -->
    <span
      v-else-if="tracking.opened"
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 whitespace-nowrap"
      :title="`Opened ${tracking.open_count} time(s)`"
    >
      <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
        <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/>
      </svg>
      Opened ({{ tracking.open_count }})
    </span>
    
    <!-- Delivered (No Opens Yet) -->
    <span
      v-else-if="tracking.delivered"
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30 whitespace-nowrap"
      title="Successfully delivered"
    >
      <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
      </svg>
      Delivered
    </span>
    
    <!-- Pending (Default) -->
    <span
      v-else
      class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 whitespace-nowrap"
      title="Email is being processed"
    >
      <svg class="w-3.5 h-3.5 mr-1.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
      </svg>
      Pending
    </span>
    
  </div>
</template>

<script>
export default {
  name: 'EmailStatusBadge',
  props: {
    tracking: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isResending: false
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    handleResend() {
      // Emit resend event to parent component with tracking data
      this.$emit('resend', this.tracking);
    }
  }
};
</script>

